import { useState, useEffect, useMemo } from 'react'
import {
  getHideChromeMode,
  getMenuExpandedMode,
  getMenuPanelWidth
} from './utils'
import { MENU_EXPANDED_STORAGE_KEY } from './constants.js'
import isTest from '@signal/utils/env/isTest'
import isStorybook from '@signal/utils/env/isStorybook'
import { useLocation } from 'react-router-dom'

/**
 * Hook which obtains whether we should hide chrome due to query parameter or session setting
 * @return {Boolean} - whether we should be in hide chrome mode
 */
export function useHideChromeMode(): boolean {
  if (isTest() || isStorybook()) {
    return getHideChromeMode(window.location.search)
  }

  // because test & storybook modes are static, this is okay
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { search } = useLocation()
  return getHideChromeMode(search)
}

/**
 * Hook which obtains whether the menu should be expanded based on localStorage setting
 * This hook will re-render when localStorage changes, ensuring reactive updates
 * @return {Boolean} - whether the menu should be in expanded mode (defaults to true)
 */
export function useMenuExpandedMode(): boolean {
  const [isExpanded, setIsExpanded] = useState(() => getMenuExpandedMode())

  useEffect(() => {
    // Listen for localStorage changes (from other tabs/windows or programmatic changes)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === MENU_EXPANDED_STORAGE_KEY) {
        setIsExpanded(getMenuExpandedMode())
      }
    }

    // Listen for custom events (for same-tab changes)
    const handleCustomStorageChange = () => {
      setIsExpanded(getMenuExpandedMode())
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('menuExpandedChanged', handleCustomStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener(
        'menuExpandedChanged',
        handleCustomStorageChange
      )
    }
  }, [])

  return isExpanded
}

/**
 * Hook which returns the current menu panel width based on hide chrome and expansion state
 * This hook will re-render when either state changes, ensuring reactive layout updates
 * @return {String} - the menu panel width (e.g., "240px", "60px", or "0px")
 */
export function useMenuPanelWidth(): string {
  const hideChrome = useHideChromeMode()
  const isExpanded = useMenuExpandedMode()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => getMenuPanelWidth(), [hideChrome, isExpanded])
}
